/**
 * Rider App — public booking + ride-management surfaces.
 * Hosts the booking flow (map + morphing pill + Stripe checkout),
 * dashboard, trip history, account settings, support + legal pages.
 *
 * Mounted by `/app/frontend/src/App.js` when the path does NOT start
 * with `/driver` or `/admin`. A single <BrowserRouter> wraps the
 * top-level dispatcher so this sub-app can declare nested routes
 * naturally without owning the router instance itself.
 */
import React, { useCallback, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import Header from "@/shared/components/Header";
import ProtectedRoute from "@/shared/components/ProtectedRoute";
import { useAuth } from "@/shared/context/AuthContext";
// MobileTabBar removed per user directive — tab pill was costing ~80px at
// bottom of every screen; Account is now reachable via the top greeting pill.
import TopAvatarPill from "@/shared/components/RiderAccountSurface";
import RouteTopSpacer from "@/shared/components/RouteTopSpacer";
import InstallPrompt from "@/shared/components/InstallPrompt";
import StandaloneRedirect from "@/shared/components/StandaloneRedirect";
import LocationPermissionPrompt from "@/shared/maps/LocationPermissionPrompt";
import PushNotificationsBootstrap from "@/shared/components/PushNotificationsBootstrap";
import AccessibilityRoot from "@/apps/rider/components/AccessibilityRoot";
import { AccountMorphProvider, AccountMorphOverlay } from "@/apps/rider/components/AccountMorph";
import { RouterTransition } from "@/shared/motion";

import About from "@/apps/rider/pages/About";
import CheckoutSuccess from "@/apps/rider/pages/CheckoutSuccess";
import CheckoutPlan from "@/apps/rider/pages/CheckoutPlan";
import RouteErrorBoundary from "@/shared/components/RouteErrorBoundary";
import Dashboard from "@/apps/rider/pages/Dashboard";
import HomePlan from "@/apps/rider/pages/HomePlan";
import RouteCard from "@/apps/rider/pages/RouteCard";
import YourWeek from "@/apps/rider/pages/YourWeek";
import DebugPush from "@/apps/rider/pages/DebugPush";
import FAQs from "@/apps/rider/pages/FAQs";
import ForgotPassword from "@/apps/rider/pages/ForgotPassword";
import Login from "@/apps/rider/pages/Login";
import LaunchReveal from "@/apps/rider/components/LaunchReveal";
import AuthCallback from "@/apps/rider/pages/AuthCallback";
import Policies from "@/apps/rider/pages/Policies";
import Profile from "@/apps/rider/pages/Profile";
import Register from "@/apps/rider/pages/Register";
import ResetPassword from "@/apps/rider/pages/ResetPassword";
import ScheduleSetup from "@/apps/rider/pages/ScheduleSetup";
import Trips from "@/apps/rider/pages/Trips";

// Cinematic onboarding (iter 66).
import Welcome from "@/apps/rider/pages/Welcome";
import Onboarding from "@/apps/rider/pages/Onboarding";
import VerifyEmail from "@/apps/rider/pages/VerifyEmail";

// Controlled-access rollout (Feb 2026): waitlist application + status.
import Waitlist from "@/apps/rider/pages/Waitlist";
import WaitlistStatus from "@/apps/rider/pages/WaitlistStatus";

// New rider-menu surfaces (iter 63).
import PaymentMethods from "@/apps/rider/pages/PaymentMethods";
import SavedPlaces from "@/apps/rider/pages/SavedPlaces";
import NotificationPrefs from "@/apps/rider/pages/NotificationPrefs";
import AccessibilityPrefs from "@/apps/rider/pages/AccessibilityPrefs";
import AppearancePrefs from "@/apps/rider/pages/AppearancePrefs";
import HelpCenter from "@/apps/rider/pages/HelpCenter";
import ContactSupport from "@/apps/rider/pages/ContactSupport";
import ReportIssue from "@/apps/rider/pages/ReportIssue";
import PrivacyPolicy from "@/apps/rider/pages/PrivacyPolicy";
import TermsOfService from "@/apps/rider/pages/TermsOfService";
import AccessibilityStatement from "@/apps/rider/pages/AccessibilityStatement";

// Platform-nav marketing pages (iter 65 — TopMenu repositioned).
import HowLinkWorks from "@/apps/rider/pages/HowLinkWorks";
import ServiceAreas from "@/apps/rider/pages/ServiceAreas";
import Safety from "@/apps/rider/pages/Safety";
import RiderGuidelines from "@/apps/rider/pages/RiderGuidelines";

export default function RiderApp() {
  // C4 · One-shot animated wordmark reveal on cold app boot. sessionStorage
  // gate ensures the reveal plays exactly once per browser session (not on
  // every route change). Auth-callback and Stripe-return URLs skip the
  // reveal so the rider doesn't see an extra frame mid-redirect.
  const [showLaunch, setShowLaunch] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      if (sessionStorage.getItem("link.launch.played") === "1") return false;
      const p = window.location.pathname;
      if (p.startsWith("/auth/google") || p.startsWith("/checkout/success")) return false;
      return true;
    } catch (_) { return false; }
  });
  const dismissLaunch = () => {
    try { sessionStorage.setItem("link.launch.played", "1"); } catch (_) { /* noop */ }
    setShowLaunch(false);
  };
  const renderRoutes = useCallback((loc) => (
      <Routes location={loc}>
        <Route path="/debug/push" element={<DebugPush />} />
        {/* Feb 2026 · Rider home is auth-conditional.
              logged-out "/"       → Welcome (marketing landing)
              logged-in  "/"       → HomePlan (YOUR PLAN card, ref 04)
              logged-in  "/account"→ HomePlan base + drawer overlay
              booking flow lives at /book only. ScheduleSetup remains
              on disk and is unchanged; we simply no longer land users
              on it by default. Legacy Dashboard is kept at /dashboard
              for admin/detail access. */}
        <Route path="/" element={<HomeRoot />} />
        <Route path="/book" element={<ScheduleSetup />} />
        <Route path="/your-week" element={<ProtectedRoute><YourWeek /></ProtectedRoute>} />
        {/* Legacy rider-menu paths — redirect into the new locations so
            shared links from emails / social keep working. */}
        <Route path="/about" element={<About />} />
        <Route path="/faqs" element={<Navigate to="/help" replace />} />
        <Route path="/policies" element={<Navigate to="/legal/privacy" replace />} />

        {/* /pricing page intentionally removed — the pricing matrix is
            confidential. Legacy links + emails redirect home so they
            don't 404. */}
        <Route path="/pricing" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<Login />} />
        {/* C2 · Emergent-managed Google OAuth landing.
            REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
            URLS, THIS BREAKS THE AUTH. */}
        <Route path="/auth/google" element={<AuthCallback />} />
        {/* /register is the canonical onboarding entry; the new progressive
            flow lives at /onboarding. We send /register straight there so
            external links + the legacy Login "Sign up" CTA keep working. */}
        <Route path="/register" element={<Navigate to="/onboarding" replace />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/onboarding-legacy" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        {/* Controlled-access rollout (Feb 2026): new riders are routed
            to /waitlist after signup. WaitlistGate (below) also kicks
            WAITLIST riders into /waitlist/status if they try to reach
            the booking surfaces. */}
        <Route path="/waitlist" element={<ProtectedRoute><Waitlist /></ProtectedRoute>} />
        <Route path="/waitlist/status" element={<ProtectedRoute><WaitlistStatus /></ProtectedRoute>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* /schedule was the legacy multi-step wizard. It now redirects
            to the canonical booking route /book. Any deep-link to
            /schedule lands there without breaking. */}
        <Route path="/schedule" element={<Navigate to="/book" replace />} />
        <Route path="/checkout/plan" element={<ProtectedRoute><CheckoutPlan /></ProtectedRoute>} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        {/* /account is rendered as a drawer OVERLAY (see FloatingChromeGate
            below) anchored on top of the auth-conditional home. Base
            page beneath the drawer matches "/" so navigating between
            them doesn't unmount the underlying surface. */}
        <Route path="/account" element={<HomeRoot />} />
        <Route path="/trips" element={<ProtectedRoute><Trips /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

        {/* Rider menu — primary actions */}
        <Route path="/payment-methods" element={<ProtectedRoute><PaymentMethods /></ProtectedRoute>} />
        <Route path="/saved-places" element={<ProtectedRoute><SavedPlaces /></ProtectedRoute>} />

        {/* Platform-nav marketing pages (TopMenu Discover section) */}
        <Route path="/how-link-works" element={<HowLinkWorks />} />
        <Route path="/service-areas" element={<ServiceAreas />} />
        <Route path="/safety" element={<Safety />} />
        <Route path="/rider-guidelines" element={<RiderGuidelines />} />

        {/* Rider menu — preferences */}
        <Route path="/preferences/notifications" element={<ProtectedRoute><NotificationPrefs /></ProtectedRoute>} />
        <Route path="/preferences/accessibility" element={<AccessibilityPrefs />} />
        <Route path="/preferences/appearance" element={<AppearancePrefs />} />

        {/* Rider menu — support */}
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/support/contact" element={<ProtectedRoute><ContactSupport /></ProtectedRoute>} />
        <Route path="/support/report" element={<ProtectedRoute><ReportIssue /></ProtectedRoute>} />

        {/* Rider menu — legal */}
        <Route path="/legal/privacy" element={<PrivacyPolicy />} />
        <Route path="/legal/terms" element={<TermsOfService />} />
        <Route path="/legal/accessibility" element={<AccessibilityStatement />} />
        {/* Legacy fallback: keep the original Policies HTML available until search engines re-index. */}
        <Route path="/legal/policies" element={<Policies />} />
        {/* And keep the FAQs page reachable for anyone with a deep bookmark — but Help is canonical. */}
        <Route path="/legal/faqs" element={<FAQs />} />
        {/* Catch-all — anything that doesn't match above lands on /welcome
            instead of a blank no-match screen (the root-cause of the
            urgent blank-screen bug). */}
        <Route path="*" element={<Navigate to="/welcome" replace />} />
      </Routes>
  ), []);
  return (
    <AccountMorphProvider>
      {showLaunch && <LaunchReveal onDone={dismissLaunch} />}
      <StandaloneRedirect />
      <PushNotificationsBootstrap />
      <AccessibilityRoot />
      <Header />
      <RouteTopSpacer />
      <RouteErrorBoundary>
      <RouterTransition
        routes={renderRoutes}
        transitionKey={(loc) => (loc.pathname === "/account" ? "/" : loc.pathname)}
      />
      </RouteErrorBoundary>
      <FloatingChromeGate />
      <InstallPrompt />
      <AccountMorphOverlay />
    </AccountMorphProvider>
  );
}

/**
 * The floating chrome (mobile tab bar + top menu + brand mark) is global
 * for the rider shell, but a few full-page surfaces own their own header
 * and would render on top of it. We hide the chrome on those paths to
 * keep the page clean. Currently: /checkout/plan, /checkout/success.
 *
 * The Account drawer is mounted here too — globally, location-driven —
 * so navigating between "/" and "/account" no longer unmounts the
 * booking surface beneath it.
 */
function FloatingChromeGate() {
  const location = useLocation();
  const hide = ["/checkout/plan", "/checkout/success"].includes(location.pathname);
  if (hide) return null;
  return (
    <>
      {/* TopAvatarPill removed per user directive — the cream "LINK
          COMMUTER / Good morning" capsule was hostile at every screen
          height. Account is now reached via the avatar node ON the
          Home route card and ON every booking-sheet step header. */}
      <LocationPermissionPrompt />
    </>
  );
}

/**
 * Feb 2026 · auth-conditional rider home.
 *   logged-out /       → Welcome (marketing landing with Sign in / Sign up)
 *   logged-in  /       → RouteCard (filled OR empty-state plan card;
 *                        NEVER dumps riders onto the map)
 * Booking still lives at /book. Legacy Dashboard stays at /dashboard.
 */
function HomeRoot() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <RouteCard /> : <Welcome />;
}
